import React from "react";
import "./PrintFormPage.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const val = (v) => (v !== undefined && v !== null && v !== "" ? v : "—");

const formatDate = (dob) => {
  if (!dob) return "—";
  try {
    return new Date(dob).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return dob;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// PrintFormPage
// Props:
//   form           — form state object from FolderData
//   folder         — folder number string
//   eduRows        — array of educational qualification rows
//   workRows       — array of work experience rows
//   savedPhoto     — URL / base64 string of candidate photo (or null)
//   savedSignature — URL / base64 string of candidate signature (or null)
// ─────────────────────────────────────────────────────────────────────────────
const PrintFormPage = ({
  form = {},
  folder = "",
  eduRows = [],
  workRows = [],
  savedPhoto = null,
  savedSignature = null,
}) => {
  return (
    <div className="pfp-wrapper">
      <div className="pfp-page">

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className="pfp-header">
          <h1>AGRA COLLEGE, AGRA</h1>
          <h2>
            स्थायी नियुक्ति हेतु आवेदन पत्र&nbsp;/&nbsp;Application for
            Permanent Appointment
          </h2>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            ZONE A — Two-column grid
            Left  : Post / Dept / Personal fields 1–9
            Right : Photo + Signature
        ══════════════════════════════════════════════════════════════════ */}
        <div className="pfp-zone-a">

          {/* ── LEFT COLUMN ─────────────────────────────────────────────── */}
          <div className="pfp-left">

            {/* Post / Dept / Folder */}
            <div className="pfp-section">
              <div className="pfp-row">
                <span className="pfp-field">
                  <span className="pfp-label">पद / Post: </span>
                  {val(form.post)}
                </span>
                <span className="pfp-field">
                  <span className="pfp-label">विभाग / Dept.: </span>
                  {val(form.vibhag)}
                </span>
                <span className="pfp-field">
                  <span className="pfp-label">Folder No.: </span>
                  {val(folder)}
                </span>
              </div>
            </div>

            {/* Personal Details — fields 1–9 */}
            <div className="pfp-section">
              <div className="pfp-section-heading">
                व्यक्तिगत विवरण / Personal Details
              </div>

              <div className="pfp-row">
                <span className="pfp-field pfp-full">
                  <span className="pfp-label">1. नाम (English): </span>
                  {val(form.nameEnglish)}
                </span>
              </div>

              <div className="pfp-row">
                <span className="pfp-field pfp-full">
                  <span className="pfp-label">2. नाम (हिंदी): </span>
                  {val(form.nameHindi)}
                </span>
              </div>

              <div className="pfp-row">
                <span className="pfp-field pfp-full">
                  <span className="pfp-label">3. पिता / पति का नाम: </span>
                  {val(form.fatherName)}
                </span>
              </div>

              <div className="pfp-row">
                <span className="pfp-field">
                  <span className="pfp-label">4. जन्म तिथि / DOB: </span>
                  {formatDate(form.dob)}
                </span>
                <span className="pfp-field">
                  <span className="pfp-label">5. लिंग / Gender: </span>
                  {val(form.gender)}
                </span>
              </div>

              <div className="pfp-row">
                <span className="pfp-field">
                  <span className="pfp-label">6. श्रेणी / Category: </span>
                  {val(form.category)}
                </span>
                <span className="pfp-field">
                  <span className="pfp-label">7. धर्म / Religion: </span>
                  {val(form.religion)}
                </span>
                <span className="pfp-field">
                  <span className="pfp-label">8. जाति / Caste: </span>
                  {val(form.caste)}
                </span>
              </div>

              <div className="pfp-row">
                <span className="pfp-field pfp-full">
                  <span className="pfp-label">
                    9. वर्तमान पता / Current Address:{" "}
                  </span>
                  {val(form.address)}
                </span>
              </div>
            </div>
          </div>
          {/* ── END LEFT COLUMN ── */}

          {/* ── RIGHT COLUMN: Photo + Signature ────────────────────────── */}
          <div className="pfp-right">

            {/* Photo */}
            <div className="pfp-photo-box">
              {savedPhoto ? (
                <img src={savedPhoto} alt="Candidate Photo" />
              ) : (
                <div className="pfp-photo-placeholder">
                  <span>📷</span>
                  <p>Paste Photo</p>
                  <small>3.5 × 4.5 cm</small>
                </div>
              )}
            </div>

            {/* Spacer */}
            <div className="pfp-right-spacer" />

            {/* Signature */}
            <div className="pfp-sign-box">
              <div className="pfp-sign-area">
                {savedSignature ? (
                  <img src={savedSignature} alt="Signature" />
                ) : (
                  <span className="pfp-sign-placeholder">हस्ताक्षर</span>
                )}
              </div>
              <p className="pfp-sign-label">आवेदक के हस्ताक्षर</p>
            </div>

          </div>
          {/* ── END RIGHT COLUMN ── */}

        </div>
        {/* ══ END ZONE A ══ */}

        {/* ══════════════════════════════════════════════════════════════════
            ZONE B — Full-width: fields 10–14 + tables + payment
        ══════════════════════════════════════════════════════════════════ */}
        <div className="pfp-zone-b">

          {/* Fields 10–14 */}
          <div className="pfp-section">
            <div className="pfp-row">
              <span className="pfp-field pfp-full">
                <span className="pfp-label">
                  10. पत्राचार पता / Correspondence Address:{" "}
                </span>
                {val(form.correspondenceAddress || form.address)}
              </span>
            </div>

            <div className="pfp-row">
              <span className="pfp-field">
                <span className="pfp-label">11. पिन कोड / Pin Code: </span>
                {val(form.pinCode)}
              </span>
              <span className="pfp-field">
                <span className="pfp-label">12. मोबाइल / Mobile: </span>
                {val(form.mobile)}
              </span>
            </div>

            <div className="pfp-row">
              <span className="pfp-field pfp-full">
                <span className="pfp-label">13. ई-मेल / Email: </span>
                {val(form.email)}
              </span>
            </div>

            <div className="pfp-row">
              <span className="pfp-field pfp-full">
                <span className="pfp-label">
                  14. आधार संख्या / Aadhar No.:{" "}
                </span>
                {val(form.aadharNumber)}
              </span>
            </div>
          </div>

          {/* Educational Qualification */}
          <div className="pfp-section">
            <div className="pfp-section-heading">
              शैक्षिक योग्यता / Educational Qualification
            </div>
            <table className="pfp-table">
              <thead>
                <tr>
                  <th>क्र.सं.</th>
                  <th>परीक्षा / Exam</th>
                  <th>बोर्ड / Board</th>
                  <th>Score Type</th>
                  <th>पूर्णांक / Total</th>
                  <th>प्राप्तांक / Obtained</th>
                  <th>% / Result</th>
                </tr>
              </thead>
              <tbody>
                {eduRows.length > 0 ? (
                  eduRows.map((row, idx) => (
                    <tr key={row.id ?? idx}>
                      <td>{idx + 1}</td>
                      <td className="pfp-table-left">
                        {row.exam === "Other"
                          ? row.customExam || row.exam
                          : row.exam || "—"}
                      </td>
                      <td>{val(row.board)}</td>
                      <td>{row.scoreType || "Percentage"}</td>
                      <td>{val(row.totalMarks)}</td>
                      <td>{val(row.obtainedMarks)}</td>
                      <td>
                        {row.percentage
                          ? `${row.percentage}${
                              row.scoreType === "Percentage" ? "%" : ""
                            }`
                          : "—"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="pfp-empty-cell">
                      कोई डेटा नहीं
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Typing & Shorthand */}
          <div className="pfp-section">
            <div className="pfp-section-heading">
              टंकण एवं आशुलिपि दक्षता / Typing &amp; Shorthand Speed
            </div>
            <div className="pfp-row">
              <span className="pfp-field">
                <span className="pfp-label">हिंदी टंकण गति: </span>
                {val(form.hindiTyping)}
              </span>
              <span className="pfp-field">
                <span className="pfp-label">अंग्रेजी टंकण गति: </span>
                {val(form.englishTyping)}
              </span>
              <span className="pfp-field">
                <span className="pfp-label">आशुलिपि गति: </span>
                {val(form.shorthandSpeed)}
              </span>
            </div>
          </div>

          {/* Work Experience */}
          <div className="pfp-section">
            <div className="pfp-section-heading">
              कार्य अनुभव / Work Experience
            </div>
            <table className="pfp-table">
              <thead>
                <tr>
                  <th>क्र.सं.</th>
                  <th>संस्था का नाम एवं पता / Organization</th>
                  <th>अवधि / Duration</th>
                  <th>पद / Post</th>
                  <th>वेतनमान / Salary</th>
                </tr>
              </thead>
              <tbody>
                {workRows.length > 0 ? (
                  workRows.map((row, idx) => (
                    <tr key={row.id ?? idx}>
                      <td>{String(idx + 1).padStart(2, "0")}</td>
                      <td className="pfp-table-left">{val(row.orgName)}</td>
                      <td>{val(row.duration)}</td>
                      <td>{val(row.post)}</td>
                      <td>{val(row.salary)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="pfp-empty-cell">
                      कोई कार्य अनुभव नहीं
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Payment Information */}
          <div className="pfp-section">
            <div className="pfp-section-heading">
              भुगतान जानकारी / Payment Information
            </div>
            <div className="pfp-row">
              <span className="pfp-field">
                <span className="pfp-label">
                  भुगतान विधि / Payment Method:{" "}
                </span>
                {val(form.paymentMethod)}
              </span>
              <span className="pfp-field">
                <span className="pfp-label">
                  भुगतान संख्या / Payment No.:{" "}
                </span>
                {val(form.paymentNumber)}
              </span>
            </div>
          </div>

        </div>
        {/* ══ END ZONE B ══ */}

      </div>
    </div>
  );
};

export default PrintFormPage;